"use client"

import React, { useCallback, useEffect, useMemo } from "react"
import clsx from "clsx"
import {
  Channel,
  ChannelList,
  MessageInput,
  Thread,
  VirtualizedMessageList,
} from "stream-chat-react"

import {
  Box,
  Loading,
  MESSENGER_URI,
  Redirect,
  useBreakpoint,
  useChannel,
  useMainInboxChannelIds,
  useMessageChannelByStreamId,
  useMessagingContext,
  useUserContext,
  useUserMessageChannel,
} from "@bullseye/components"
import { usePathname, useRouter, useSearchParams } from "@bullseye/navigation"

import {
  MessageChannelHeader,
  MessengerChannelPreview,
  MessengerInput,
  MessengerUserSuspended,
  NoChannelSelected,
  StreamChatAvatar,
} from "../../components"

type Channel = {
  id: string
  data?: {
    hidden?: boolean
  }
}

const A11yScroller = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => <div {...props} ref={ref} role="group" />)
A11yScroller.displayName = "A11yScroller"

const MessengerPage = () => {
  const { user } = useUserContext()
  const {
    currentChannelId,
    setCurrentChannelId,
    enableChannelLists,
    isLoadingChannelLists,
  } = useMessagingContext()

  const isSm = useBreakpoint("md", "down")

  const sp = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()

  const channelId = useMemo(
    () => sp?.get("channel") ?? undefined,
    [sp?.get("channel")],
  )

  const { channel: data, isLoading: channelLoading } = useChannel(channelId)
  const channel = data as Channel
  const { messageChannel } = useMessageChannelByStreamId(channel?.id)
  const { userMessageChannel } = useUserMessageChannel(messageChannel?.id)

  const userIsSuspended = messageChannel?.dm_user?.is_suspended || false

  const mainInboxChannelIds = useMainInboxChannelIds()

  // Keep channel IDs stable - don't reorder based on selection
  const channelIds = mainInboxChannelIds

  // Enable lazy loading of channel lists when messenger page mounts
  useEffect(() => {
    enableChannelLists()
  }, [enableChannelLists])

  const renderPreview = useCallback(
    ({ channel = null, lastMessage = null, unread = null }: any) => {
      return (
        <MessengerChannelPreview
          channelId={(channel as Channel).id}
          unread={unread}
          lastMessage={lastMessage}
        />
      )
    },
    [],
  )

  useEffect(() => {
    if (!!channelId && currentChannelId !== channelId) {
      setCurrentChannelId(channelId)
    }
  }, [channelId, currentChannelId, setCurrentChannelId])

  const handleAcceptMessage = useCallback(() => {
    router.replace(`${pathname}?channel=${channelId}`)
  }, [router, pathname, channelId])

  if (channel?.data?.hidden) {
    return <Redirect href={MESSENGER_URI} push />
  }

  return (
    <Box className="flex h-full w-full flex-col overflow-scroll">
      <Box className="hidden md:flex">
        <MessageChannelHeader messageChannel={messageChannel} />
      </Box>
      <Box className="flex h-full w-full flex-1 flex-row">
        <Box
          className={clsx("flex w-full overflow-hidden md:w-[30%]", {
            hidden: isSm && channelId,
          })}
        >
          <Box className="flex md:hidden">
            <MessageChannelHeader messageChannel={messageChannel} />
          </Box>
          {isLoadingChannelLists ? (
            <Box className="flex h-full w-full items-center justify-center p-8">
              <Loading />
            </Box>
          ) : (
            <ChannelList
              filters={{
                members: {
                  $in: [user.stream_id],
                },
                id: {
                  $in:
                    channelIds.length > 0
                      ? channelIds
                      : ["fake-id-does-not-exist"],
                },
              }}
              sort={{
                last_message_at: -1,
              }}
              Preview={renderPreview}
              customActiveChannel={channelId}
              setActiveChannelOnMount={false}
            />
          )}
        </Box>
        <NoChannelSelected hidden={!!channelId} />
        <Box
          className={clsx("w-full overflow-hidden md:flex md:w-[70%]", {
            hidden: isSm && !channelId,
            "md:hidden": !channelId,
          })}
        >
          <Channel channel={channel as any} Avatar={StreamChatAvatar}>
            <Box className="dvh-full h-full w-full md:h-full">
              <Box className="flex md:hidden">
                <MessageChannelHeader messageChannel={messageChannel} />
              </Box>
              <MessengerUserSuspended hidden={!userIsSuspended} />
              <Box
                className={clsx("flex-1 pt-4", {
                  hidden: userIsSuspended,
                })}
              >
                {channelLoading ? (
                  <Box className="flex h-full w-full items-center justify-center">
                    <Loading />
                  </Box>
                ) : (
                  <VirtualizedMessageList
                    scrollToLatestMessageOnFocus
                    additionalVirtuosoProps={{
                      components: { Scroller: A11yScroller as any },
                    }}
                  />
                )}
              </Box>
              <MessengerInput
                messageChannel={messageChannel}
                userMessageChannel={userMessageChannel}
                InputComponent={MessageInput}
                handleAcceptMessage={handleAcceptMessage}
              />
            </Box>
            <Thread />
          </Channel>
        </Box>
      </Box>
    </Box>
  )
}

export default MessengerPage